import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useCapexPlans } from '../../hooks/useCapexQueries'
import { CreatePlanDialog } from './CreatePlanDialog'
import { TheoreticalPlanView } from './TheoreticalPlanView'
import { EmptyState } from '../ui/empty-state'
import { mdiArrowRight, mdiLockOutline, mdiPlus } from '@mdi/js'
import { SECTION_ICONS } from '../../lib/section-icons'

interface AssetCapexPlansProps {
  org: string
  assetUid: string
}

type WorkflowStatus = 'draft' | 'submitted' | 'approved' | 'rejected'

const WORKFLOW_BADGE_VARIANTS: Record<WorkflowStatus, 'secondary' | 'default' | 'destructive'> = {
  draft: 'secondary',
  submitted: 'default',
  approved: 'default',
  rejected: 'destructive',
}

function WorkflowStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('capex')
  const variant = WORKFLOW_BADGE_VARIANTS[status as WorkflowStatus] ?? 'secondary'
  return (
    <Badge variant={variant}>
      {t(`workflow.${status}`, status)}
    </Badge>
  )
}

export function AssetCapexPlans({ org, assetUid }: AssetCapexPlansProps) {
  const { t } = useTranslation('capex')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const { data: allPlans, isLoading, error } = useCapexPlans(org, assetUid)
  const { data: archivedPlans } = useCapexPlans(org, assetUid, { archived: true })

  // Active plans are non-archived (backend returns non-archived by default)
  const activePlans: any[] = allPlans ?? []
  const archived: any[] = (archivedPlans ?? []).filter(
    (p: any) => !activePlans.find((a: any) => a.uid === p.uid),
  )

  const formatHorizon = (plan: any) =>
    `${plan.horizon_start}–${plan.horizon_end}`

  return (
    <div className="space-y-6">
      {/* Permanent read-only reference trajectory, computed live from equipment.
          Header mirrors the Simulations section: title left, status action right. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">{t('theoretical.title')}</h3>
          <Button
            size="sm"
            variant="outline"
            disabled
            aria-label={t('theoretical.readonly')}
            title={t('theoretical.readonly')}
            data-testid="theoretical-readonly-btn"
          >
            <AppIcon path={mdiLockOutline} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('theoretical.readonly')}</span>
          </Button>
        </div>
        <TheoreticalPlanView org={org} assetUid={assetUid} />
      </div>

      {/* Simulations: editable plans that enter the submit/approve/archive circuit. */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t('simulations.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('simulations.subtitle')}</p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreateDialogOpen(true)}
          data-testid="create-capex-plan-btn"
        >
          <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
          <span className="hidden md:inline">{t('simulations.start')}</span>
        </Button>
      </div>

      {/* Filters box */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <Select
          value={showArchived ? 'all' : 'active'}
          onValueChange={(v) => setShowArchived(v === 'all')}
        >
          <SelectTrigger className="w-auto h-9 gap-1" data-testid="capex-archived-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid="capex-archived-active">{t('asset_tab.filter_active')}</SelectItem>
            <SelectItem value="all" data-testid="capex-archived-all">{t('asset_tab.filter_all')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive text-sm">
              {t('asset_tab.load_error')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Active plans */}
      {!isLoading && !error && activePlans.length === 0 && (
        <EmptyState
          variant="inline"
          className="py-12"
          icon={SECTION_ICONS.capex}
          title={t('asset_tab.no_plans')}
          data-testid="capex-empty"
        />
      )}

      {activePlans.map((plan: any) => (
        <Card
          key={plan.uid}
          className="border-primary/30"
          data-testid={`capex-plan-card-${plan.uid}`}
        >
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                <CardTitle className="text-base">{plan.name}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('horizon')}: {formatHorizon(plan)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {plan.version !== undefined && (
                  <Badge variant="outline">
                    v{plan.version}
                  </Badge>
                )}
                <WorkflowStatusBadge status={plan.workflow_status ?? 'draft'} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {plan.worksites !== undefined && (
                  <span>
                    {Array.isArray(plan.worksites)
                      ? plan.worksites.length
                      : plan.worksites}{' '}
                    {t('asset_tab.worksites')}
                  </span>
                )}
              </div>
              <Link
                to="/$org/capex-plans/$planUid"
                params={{ org, planUid: plan.uid }}
                data-testid={`view-capex-plan-btn-${plan.uid}`}
              >
                <Button variant="outline" size="sm">
                  {t('asset_tab.view_plan')}
                  <AppIcon path={mdiArrowRight} size="sm" className="ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ))}

      {showArchived && archived.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            {t('asset_tab.archived_plans')}
          </h4>
          {archived.map((plan: any) => (
            <Card
              key={plan.uid}
              className="opacity-70"
              data-testid={`capex-plan-card-${plan.uid}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-foreground">{plan.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatHorizon(plan)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <WorkflowStatusBadge status={plan.workflow_status ?? 'draft'} />
                    <Link
                      to="/$org/capex-plans/$planUid"
                      params={{ org, planUid: plan.uid }}
                      data-testid={`view-capex-plan-btn-${plan.uid}`}
                    >
                      <Button variant="ghost" size="sm">
                        <AppIcon path={mdiArrowRight} size="sm" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreatePlanDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        org={org}
        assetUid={assetUid}
      />
    </div>
  )
}
